import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router";
import { toast } from "sonner";
import { isFeatureEnabled } from "@shared-core/config/features";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CatalogueItemTile } from "@shared-core/data/catalogue-types";
import { useCanCurateCatalogue } from "../app/auth/hooks";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { Input } from "../app/components/primitives/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../app/components/primitives/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { Tabs, TabsList, TabsTrigger } from "../app/components/primitives/tabs";
import { CatalogueCustomerContext } from "../app/components/domain/catalogue/CatalogueCustomerContext";
import { useCatalogueContext } from "../app/components/domain/catalogue/useCatalogueContext";
import { CatalogueItemGrid } from "../app/components/domain/catalogue/CatalogueItemGrid";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { ChevronDown, Plus, Search, SlidersHorizontal, X } from "lucide-react";

// The cross-catalogue browse page. Every piece of browse state — tab,
// catalogue filter, search, sort, page — lives in the URL, so any view is
// shareable and survives refresh/back. Filtering, sorting, searching, and
// paging all run server-side (docs/list-endpoints.md); this page only renders
// what the adapter returns.

// 24 divides the grid's 2- and 3-column layouts evenly (25 leaves an orphan).
const PAGE_SIZE = 24;

const SORT_OPTIONS = [
  // "" = the backend default: catalogues alphabetically, then each
  // catalogue's curated item order.
  { value: "catalogue", label: "Catalogue order" },
  { value: "-created_at", label: "Newest first" },
  { value: "name", label: "Name A–Z" },
  { value: "-name", label: "Name Z–A" },
] as const;

export function CataloguePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const canCurate = useCanCurateCatalogue();
  const adapter = useDataAdapter();
  const context = useCatalogueContext();
  const customerCode = context.customer?.code ?? "";
  const catalogues = context.catalogues;

  // ── URL state (single source of truth) ──────────────────────────────────
  const tab = searchParams.get("tab") === "favourites" ? "favourites" : "all";
  const cataloguesParam = searchParams.get("catalogues") ?? "";
  const selectedCatalogueIds = cataloguesParam.split(",").filter(Boolean);
  const q = searchParams.get("q") ?? "";
  const sort = searchParams.get("sort") ?? "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null || value === "") next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  // Legacy deep links: ?scope=<id> was the old single-catalogue switcher.
  useEffect(() => {
    const scope = searchParams.get("scope");
    if (scope) updateParams({ scope: null, catalogues: scope });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data ─────────────────────────────────────────────────────────────────
  const [tiles, setTiles] = useState<CatalogueItemTile[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped to force a refetch (e.g. after a failed favourite toggle).
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTiles([]);
    setLoadFailed(false);
    if (context.loading || context.failed) return;
    adapter
      .listCatalogueItems({
        page,
        pageSize: PAGE_SIZE,
        orderingCustomerCode: customerCode,
        sort: sort || undefined,
        q: q || undefined,
        catalogueIds:
          selectedCatalogueIds.length > 0 ? selectedCatalogueIds : undefined,
        favouritesOnly: tab === "favourites" ? true : undefined,
      })
      .then((result) => {
        if (cancelled) return;
        setTiles(result.items);
        setTotal(result.total);
        setTotalPages(result.totalPages);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // selectedCatalogueIds is derived from cataloguesParam — dep on the string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adapter,
    tab,
    cataloguesParam,
    q,
    sort,
    page,
    refreshTick,
    customerCode,
    context.loading,
    context.failed,
  ]);

  // ── Search input (debounced into the URL) ────────────────────────────────
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => setSearchInput(q), [q]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== q) updateParams({ q: searchInput, page: null });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const toggleCatalogue = (catalogueId: string, selected: boolean) => {
    const next = selected
      ? [...selectedCatalogueIds, catalogueId]
      : selectedCatalogueIds.filter((id) => id !== catalogueId);
    updateParams({ catalogues: next.join(","), page: null });
  };

  const hasActiveFilters = !!q || selectedCatalogueIds.length > 0 || !!sort;
  const clearFilters = () =>
    updateParams({ q: null, catalogues: null, sort: null, page: null });

  const handleToggleFavourite = async (itemId: string, next: boolean) => {
    // Optimistic: flip in place; on the favourites tab an unfavourite
    // removes the tile from view.
    if (tab === "favourites" && !next) {
      setTiles((prev) => prev.filter((t) => t.item.id !== itemId));
      setTotal((t) => Math.max(0, t - 1));
    } else {
      setTiles((prev) =>
        prev.map((t) =>
          t.item.id === itemId ? { ...t, isFavourite: next } : t,
        ),
      );
    }
    try {
      if (next) await adapter.favouriteCatalogueItem(itemId);
      else await adapter.unfavouriteCatalogueItem(itemId);
    } catch {
      toast.error("Couldn't update favourite");
      setRefreshTick((n) => n + 1);
    }
  };

  // Manage: one selected catalogue opens that catalogue's record on One; otherwise the catalogues list.
  const manageTarget =
    selectedCatalogueIds.length === 1
      ? paths.objectRecord({
          objectId: "catalogue.catalogue",
          recordKey: selectedCatalogueIds[0],
        })
      : paths.objectList({ objectId: "catalogue.catalogue" });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1>Catalogue</h1>
          <p className="text-sm text-muted-foreground">
            Choose a product, configure your options and check the price.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canCurate && (
            <Link to={manageTarget}>
              <Button variant="outline" size="sm">
                <SlidersHorizontal className="size-4 mr-1" />
                {selectedCatalogueIds.length === 1
                  ? "Manage catalogue"
                  : "Manage catalogues"}
              </Button>
            </Link>
          )}
          {isRouteEnabled("createEstimate") &&
            isFeatureEnabled("merchandise_draft_estimates") && (
              <Link to={paths.createEstimate()}>
                <Button variant="outline" size="sm">
                  <Plus className="size-4 mr-1" />
                  New merchandise request
                </Button>
              </Link>
            )}
        </div>
      </div>

      <CatalogueCustomerContext
        customer={context.customer}
        onChange={context.selectCustomer}
      />

      {!context.loading &&
        !context.failed &&
        context.customer &&
        !catalogues.some((c) => c.audience !== "full_range") && (
          <p role="status" className="text-sm text-muted-foreground">
            No catalogue is allocated to {context.customer.name}. The full range
            lists everything we offer, with pricing checked for{" "}
            {context.customer.name}.
          </p>
        )}

      {/* Tabs */}
      <Tabs
        value={tab}
        onValueChange={(value) =>
          updateParams({ tab: value === "all" ? null : value, page: null })
        }
      >
        <TabsList variant="underline">
          <TabsTrigger value="all">All items</TabsTrigger>
          <TabsTrigger value="favourites">My favourites</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Toolbar: search · catalogue multi-select · sort */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative sm:max-w-xs w-full">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search items and catalogues…"
            aria-label="Search items and catalogues"
            className="pl-8"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              {selectedCatalogueIds.length > 0
                ? `Catalogues · ${selectedCatalogueIds.length}`
                : "Filter products by catalogue"}
              <ChevronDown className="size-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {catalogues.map((catalogue) => (
              <DropdownMenuCheckboxItem
                key={catalogue.id}
                checked={selectedCatalogueIds.includes(catalogue.id)}
                onCheckedChange={(checked) =>
                  toggleCatalogue(catalogue.id, !!checked)
                }
                // Keep the menu open so several catalogues can be picked in
                // one visit.
                onSelect={(e) => e.preventDefault()}
              >
                {catalogue.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Select
          value={sort || "catalogue"}
          onValueChange={(value) =>
            updateParams({
              sort: value === "catalogue" ? null : value,
              page: null,
            })
          }
        >
          <SelectTrigger size="sm" className="w-44" aria-label="Sort items">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        )}

        <div
          className="ml-auto text-sm text-muted-foreground whitespace-nowrap"
          role="status"
        >
          {context.failed || loadFailed
            ? ""
            : loading
              ? "Loading products…"
              : `${total.toLocaleString()} item${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {selectedCatalogueIds.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2"
          aria-label="Active catalogue filters"
        >
          <span className="text-sm text-muted-foreground">Showing:</span>
          {selectedCatalogueIds.map((id) => {
            const name =
              catalogues.find((catalogue) => catalogue.id === id)?.name ?? id;
            return (
              <Button
                key={id}
                variant="secondary"
                size="sm"
                className="h-auto min-h-8 whitespace-normal text-left"
                onClick={() => toggleCatalogue(id, false)}
                aria-label={`Remove ${name} filter`}
              >
                {name}
                <X className="size-3.5 shrink-0" aria-hidden="true" />
              </Button>
            );
          })}
        </div>
      )}

      {/* Results */}
      {context.failed ? (
        <Card className="p-12 space-y-3" role="alert">
          <p>
            Catalogue availability could not be checked for this customer
            context.
          </p>
          <Button variant="outline" onClick={context.retry}>
            Try again
          </Button>
        </Card>
      ) : loading && tiles.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : loadFailed ? (
        <Card className="p-12 text-center space-y-3" role="alert">
          <div className="text-sm text-muted-foreground">
            The catalogue couldn&apos;t be loaded.
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshTick((n) => n + 1)}
          >
            Try again
          </Button>
        </Card>
      ) : tiles.length === 0 ? (
        <EmptyState
          tab={tab}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />
      ) : (
        <>
          <CatalogueItemGrid
            tiles={tiles}
            manage={false}
            onToggleFavourite={handleToggleFavourite}
            itemSearch={context.search}
          />
          <ListPagination
            page={page}
            totalPages={totalPages}
            onPageChange={(nextPage) =>
              updateParams({ page: nextPage === 1 ? null : String(nextPage) })
            }
          />
        </>
      )}
    </div>
  );
}

function EmptyState({
  tab,
  hasActiveFilters,
  onClearFilters,
}: {
  tab: "all" | "favourites";
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}) {
  if (hasActiveFilters) {
    return (
      <Card className="p-12 text-center space-y-3">
        <div className="text-sm text-muted-foreground">
          No items match your search or filters.
        </div>
        <Button variant="outline" size="sm" onClick={onClearFilters}>
          Clear filters
        </Button>
      </Card>
    );
  }
  if (tab === "favourites") {
    return (
      <Card className="p-12 text-center text-sm text-muted-foreground">
        No favourites yet — tap the star on any item to keep it here.
      </Card>
    );
  }
  return (
    <Card className="p-12 text-center text-sm text-muted-foreground">
      No items in the catalogue yet.
    </Card>
  );
}
